<template>
  <model-form
    ref="form"
    :model="modifier"
    modal-type="modal"
    @on-show="activeTab = 'details'"
  >
    <q-tabs
      v-if="!$_.isNil(currentSite.activeInventoryStrategy)"
      v-model="activeTab"
      class="text-primary"
      active-color="primary"
      indicator-color="primary"
      align="justify"
    >
      <q-tab
        name="details"
        label="Details"
      />
      <q-tab
        v-if="currentSite.activeInventoryStrategy"
        name="inventory"
        label="Inventory"
      />
    </q-tabs>

    <q-tab-panels
      v-model="activeTab"
      animated
    >
      <q-tab-panel name="details">
        <div class="column">
          <div class="row q-col-gutter-sm">
            <model-form-input
              field="name"
              class="col-12"
              label="Name"
              :rules="[val => !!val || 'Field is required']"
              autofocus
            />
          </div>
          <div class="row q-col-gutter-sm">
            <div
              class="col-6"
              style="min-height: 150px; overflow: hidden;"
            >
              <model-form-image-uploader
                field="image"
                :image-url="modifier.imageUrl"
                ratio="1"
              />
            </div>
            <div class="col-6">
              <color-picker
                color-name="custom"
                :color-hex="modifier.color"
                :disable="modifier.isTranslation"
                @update:model-value="value => modifier.color = value"
              />
            </div>
          </div>
          <div class="row q-col-gutter-sm">
            <model-form-input
              field="description"
              class="col-12"
              type="textarea"
              label="Description"
            />
          </div>
          <div class="row q-col-gutter-sm">
            <model-form-input
              field="price"
              class="col-6"
              label="Price"
              :prefix="currentCircuit.localeCurrencySymbol"
              type="number"
              min="0"
              step="0.25"
              :rules="[val => val !== null || 'Field is required']"
            />
            <model-form-input
              field="cost"
              :disable="modifier.isRemote && modifier.disableEditCostCents !== false"
              class="col-6"
              label="Cost"
              :prefix="currentCircuit.localeCurrencySymbol"
              type="number"
              min="0"
              step="0.01"
            >
              <q-tooltip v-if="modifier.isRemote && modifier.disableEditCostCents !== false">
                Cost is controlled by the inventory items that are associated through inventory recipes.
              </q-tooltip>
            </model-form-input>
          </div>
          <div class="row items-center q-col-gutter-sm">
            <model-form-checkbox
              field="availableOnline"
              class="col-6 q-pb-sm"
              label="Available Online"
            >
              <q-tooltip>
                Show this modifier for items sold online.
              </q-tooltip>
            </model-form-checkbox>
            <model-form-checkbox
              field="salesDisabled"
              class="col-6 q-pb-sm"
              label="Disable Sales"
            />
          </div>
          <div class="row q-col-gutter-sm">
            <model-form-select
              field="modifierSetIds"
              class="col-12"
              label="Modifier Sets"
              :options="filteredModifierSets"
              option-label="name"
              option-value="id"
              map-options
              emit-value
              use-input
              input-style="search"
              input-debounce="100"
              multiple
              use-chips
              clearable
              bottom-slots
              :disable="disableModifierSets"
              @filter="filterModifierSets"
              @clear="modifier.modifierSetIds = []"
            >
              <q-tooltip>
                Select Modifier Sets that this Modifier belongs to.
              </q-tooltip>
            </model-form-select>
          </div>
        </div>
      </q-tab-panel>

      <q-tab-panel name="inventory">
        <div class="row q-col-gutter-sm">
          <model-form-checkbox
            v-if="modifier.hasRecipe"
            field="hasRecipe"
            class="col-6"
            label="Track Stock"
            disable
          />
          <model-form-checkbox
            v-else-if="currentSite.activeInventoryStrategy"
            field="action"
            class="col-6"
            label="Track Stock"
            :true-value="'manage_inventory'"
            :false-value="null"
          />
        </div>
        <div
          v-if="modifier.action === 'manage_inventory'"
          class="text-negative q-mt-md"
        >
          Important! Marking this sales item modifier with "track stock" is
          recommended when a sale should reduce the balance of a <span
            class="text-italic"
          >single</span> inventory item (for example, a can
          of soda or a bag of candy). If a sale should reduce the balances of
          <span class="text-italic">multiple</span> inventory items (for example
          selling a large popcorn might reduce balances for popcorn kernels,
          popcorn salt, oil, large popcorn bags, etc.), we recommend creating
          the inventory items and recipe manually.
        </div>
        <availabilities-and-constraints
          v-if="modifier"
          :item-variation-or-modifier="modifier"
        />
      </q-tab-panel>
    </q-tab-panels>
  </model-form>
</template>

<script>
import ColorPicker from 'src/modules/admin/components/ColorPicker'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import AvailabilitiesAndConstraints from 'src/modules/admin/item/components/AvailabilitiesAndConstraints'

export default {
  name: 'ModifierForm',
  components: {
    ColorPicker,
    ModelForm,
    AvailabilitiesAndConstraints
  },
  configuration: ['currentSite', 'currentCircuit', 'modifierSets'],
  props: {
    disableModifierSets: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      modifierSetSearchString: '',
      activeTab: 'details'
    }
  },
  models: {
    modifier: {
      modifierSets: {
        fields: ['id', 'name']
      },
      inventoryRecipes: true,
      inventoryAvailabilitiesAndConstraintsByLocation: true,
      locations: true,
      disableEditCostCents: true
    }
  },
  computed: {
    activeModifierSets () {
      return this.modifierSets.data.filter(modifierSet => _.isNil(modifierSet.archivedAt))
    },
    filteredModifierSets () {
      if (_.isEmpty(this.modifierSetSearchString)) {
        return this.activeModifierSets
      }

      return _.filter(this.activeModifierSets, modifierSet => _.includes(modifierSet.name.toLowerCase(), this.modifierSetSearchString))
    }
  },
  methods: {
    filterModifierSets (val, update) {
      update(() => {
        this.modifierSetSearchString = val.toLowerCase()
      })
    }
  }
}
</script>
